import { Card, Typography, Loader, Checkbox, Dropdown, Input, Button } from '@design-system/index';
import '../../DetailsPage.scss';
import { ColumnDef } from '@tanstack/react-table';
import { Contribution, ContributionToCreate, ContributionType, PaymentType } from '@services/member/member/member.types';
import { formatMoney } from '@utils/numbers';
import { useDetailsContributionsVM } from './useDetailsContributionsVM';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { Icon } from '@design-system/index';
import { UnarchiveLine, UnarchiveFilled, ArchiveLine, ArchiveFilled, ModifierLine, ModifierFull } from '@icons';
import { Formik } from 'formik';
import { CONTRIBUTION_PRICE, PAYMENT_TYPE } from '@utils/enumTranslation';
import moment from 'moment';

export default function DetailsContributions() {
    const {
        isLoading,
        isError,
        member,
        contributionColumnHelper,
        isArchived,
        mutationHook,
        handleArchiveContributions,
        id,
        needReload,
        handleMetadata,
        setIsArchived,
        MySwal,
        addMemberContribution,
        updateMemberContribution
    } = useDetailsContributionsVM();

    const handleContribution = async (contribution?: Contribution) => {
        await MySwal.fire<ContributionToCreate>({
            title: contribution ? "Modification d'une cotisation" : "Création d'une cotisation",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: contribution ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            heightAuto: false,
            html: (
                <Formik
                    initialValues={{
                        paymentType: contribution?.paymentType || '',
                        observation: contribution?.observation || '',
                        dateContributed: contribution?.dateContributed
                            ? moment(contribution.dateContributed).format('YYYY-MM-DD')
                            : undefined,
                        amount:
                            contribution?.amount ||
                            CONTRIBUTION_PRICE[member?.administrativeInformation.contributionType as ContributionType] ||
                            null,
                        year: contribution?.year || new Date().getFullYear(),
                        isArchived: contribution?.isArchived || false
                    }}
                    onSubmit={(values) => {
                        if (!contribution) {
                            addMemberContribution({
                                id: String(id),
                                contribution: {
                                    paymentType: values.paymentType as PaymentType,
                                    observation: values.observation,
                                    amount: values.amount || 0,
                                    year: values.year,
                                    isArchived: false,
                                    dateContributed: values.dateContributed || undefined
                                }
                            });
                        } else {
                            updateMemberContribution({
                                id: String(id),
                                contribution: {
                                    id: contribution.id,
                                    paymentType: values.paymentType as PaymentType,
                                    observation: values.observation,
                                    amount: values.amount || 0,
                                    year: values.year,
                                    isArchived: false,
                                    dateContributed: values.dateContributed || undefined
                                }
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit, setFieldValue }) => (
                        console.log('values', values),
                        (
                            <form id="form-contribution" onSubmit={handleSubmit}>
                                <Dropdown
                                    name="paymentType"
                                    label="Type de paiement"
                                    value={values.paymentType}
                                    onOptionChange={(option) => {
                                        setFieldValue('paymentType', option.id);
                                    }}
                                    options={Object.entries(PAYMENT_TYPE).map(([key, value]) => ({
                                        label: value,
                                        value: key,
                                        id: key
                                    }))}
                                />
                                <Input
                                    type="number"
                                    placeholder="Année"
                                    name="year"
                                    required
                                    label="Année"
                                    value={values.year}
                                    onChange={handleChange}
                                />
                                <Input
                                    type="number"
                                    name="amount"
                                    label="Montant"
                                    required
                                    placeholder="Montant"
                                    rightContent={'€'}
                                    value={values.amount ?? ''}
                                    onChange={handleChange}
                                    min={0}
                                />
                                <Input
                                    name="observation"
                                    label="Commentaire"
                                    placeholder="Commentaire"
                                    value={values.observation}
                                    onChange={handleChange}
                                />
                                <Input
                                    type="date"
                                    name="dateContributed"
                                    label="Date de paiement"
                                    value={values.dateContributed || ''}
                                    onChange={handleChange}
                                />
                            </form>
                        )
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-contribution') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const contributionsColumns = [
        contributionColumnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        contributionColumnHelper.accessor('year', {
            header: 'Année',
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>
        }),
        contributionColumnHelper.accessor('dateContributed', {
            header: 'Date de paiement',
            enableSorting: true,
            cell: (info) => {
                const value = info.getValue();
                return <>{value ? moment(value).format('DD/MM/YYYY') : ''}</>;
            }
        }),
        contributionColumnHelper.accessor('amount', {
            header: 'Montant',
            enableSorting: true,
            cell: (info) => <>{formatMoney(info.getValue())} </>
        }),
        contributionColumnHelper.accessor('paymentType', {
            header: 'Nature',
            enableSorting: true,
            cell: (info) => <>{PAYMENT_TYPE[info.getValue() as PaymentType]} </>
        }),
        contributionColumnHelper.accessor('observation', {
            header: 'Commentaire',
            cell: (info) => <>{info.getValue()} </>
        }),
        contributionColumnHelper.accessor('id', {
            header: 'Actions',
            enableSorting: true,
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleContribution(info.row.original);
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={
                                isArchived ? (
                                    <Icon icon={<UnarchiveFilled />} size={20} color="primary" />
                                ) : (
                                    <Icon icon={<ArchiveFilled />} size={20} color="primary" />
                                )
                            }
                            icon={
                                isArchived ? (
                                    <Icon icon={<UnarchiveLine />} size={20} color="primary" />
                                ) : (
                                    <Icon icon={<ArchiveLine />} size={20} color="primary" />
                                )
                            }
                            onClick={() => {
                                handleArchiveContributions([info.row.original], !isArchived);
                            }}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<Contribution>[];

    if (isLoading) {
        return <Loader className="full-container-loader" />;
    }

    if (isError) {
        return (
            <Typography intrinsic="p" color="secondary">
                Une erreur est survenue lors de la récupération des informations de la fiche adhérent
            </Typography>
        );
    }

    if (!member) {
        return <></>;
    }

    return (
        <div className="details-member-container">
            <div className="details-member-container-body">
                <Card headerType="very-light" headerTitle="Cotisations">
                    <MutationTable
                        noHeader
                        key={isArchived ? 'archived' : 'active'}
                        columns={contributionsColumns}
                        mutationHook={mutationHook}
                        mutationHookParams={{ id: id }}
                        defaultSorting={{
                            field: 'year',
                            direction: 'desc'
                        }}
                        className="no-card"
                        needReload={needReload}
                        createButtonOnClick={() => handleContribution()}
                        createButtonLabel="Ajouter une cotisation"
                        archiveButtonOnClick={() => setIsArchived(!isArchived)}
                        archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                        onMetadata={handleMetadata}
                        deleting={{
                            enabled: isArchived,
                            entity: 'member_contribution'
                        }}
                        footer={{
                            actions: [
                                {
                                    label: isArchived ? 'Désarchiver' : 'Archiver',
                                    icon: isArchived ? (
                                        <Icon icon={<UnarchiveLine />} size={20} color="primary" />
                                    ) : (
                                        <Icon icon={<ArchiveLine />} size={20} color="primary" />
                                    ),
                                    hoverIcon: isArchived ? (
                                        <Icon icon={<UnarchiveFilled />} size={20} color="primary" />
                                    ) : (
                                        <Icon icon={<ArchiveFilled />} size={20} color="primary" />
                                    ),
                                    onClick: (lines: Contribution[]) => handleArchiveContributions(lines, !isArchived)
                                }
                            ]
                        }}
                    />
                </Card>
            </div>
        </div>
    );
}
